// @flow
import React from 'react';
import { Modal, Button, Checkbox, message } from 'antd';
import store from '../../general/store';
import {
  postParticipant,
  assignParticipantToCoachForSchedules,
  participantSliceActions,
} from '../../slices/participantSlice';
import { addParticipantCoach } from '../../slices/coachesSlice';
import { ParticipantStatusTypesEnum } from '../../utilities/participantStatusTypesEnum';
import { showSkippedToast } from '../../utilities/scheduleTypeToast';

type Props = {
  visible: boolean,
  onClose: () => void,
  participantId: string, // compound "userId_scheduleId"
  coachScheduleId: number, // ScheduledCoach PK — used for single-type path
  coachUserId: number, // User.id — used for multi-type path
  currentScheduleTypeId: number,
  programScheduleTypes: Array<{ id: number, type: string }>,
  date: string, // YYYY-MM-DD
  isMaster: boolean,
};

const AddParticipantScheduleTypesModal = ({
  visible,
  onClose,
  participantId,
  coachScheduleId,
  coachUserId,
  currentScheduleTypeId,
  programScheduleTypes,
  date,
  isMaster,
}: Props) => {
  const [selectedScheduleTypes, setSelectedScheduleTypes] = React.useState([
    currentScheduleTypeId,
  ]);
  const [applying, setApplying] = React.useState(false);

  React.useEffect(() => {
    if (!visible) return;
    // Reset selection to the current type each time the modal opens.
    setSelectedScheduleTypes([currentScheduleTypeId]);
  }, [visible, currentScheduleTypeId]);

  // Show every schedule type in the program. The backend silently skips any
  // type where the dropped coach has no ScheduledCoach record and reports
  // skipped_schedule_type_ids in the response — surfaced as an "X of Y" warning
  // after Apply. This gives admins the full set of choices and honest feedback
  // when their selection includes a type the coach isn't on yet.
  const visibleScheduleTypes = programScheduleTypes;

  const handleApply = async () => {
    setApplying(true);
    let succeeded = false;
    try {
      const onlyCurrentChecked =
        selectedScheduleTypes.length === 1 &&
        selectedScheduleTypes[0] === currentScheduleTypeId;

      if (onlyCurrentChecked) {
        // Backward-compatible single-drop path — uses ScheduledCoach PK
        await store.dispatch(
          postParticipant(
            participantId,
            coachScheduleId,
            ParticipantStatusTypesEnum.SCHEDULED,
            isMaster
          )
        );
        message.success('Participant assigned');
      } else {
        // Optimistic update for the currently-viewed schedule type so the UI
        // reflects the assignment without waiting for the round trip. Mirrors
        // the dual dispatch in postParticipant (participantSlice.js:348-349) —
        // the participant slice and the coaches slice both need updating in
        // lockstep because the coach card reads participants from the coach's
        // scheduledParticipants array, not from the participant slice.
        // Server response reconciles state for all checked types via
        // updateParticipantForAllSchedules inside the thunk.
        store.dispatch(
          participantSliceActions.updateParticipant({
            scheduledCoach: coachScheduleId,
            id: participantId,
          })
        );
        store.dispatch(
          addParticipantCoach({
            coachId: coachScheduleId,
            participantId,
          })
        );

        const result = await store.dispatch(
          assignParticipantToCoachForSchedules(
            participantId,
            coachUserId,
            selectedScheduleTypes,
            date,
            isMaster
          )
        );
        const skipped = result?.skippedScheduleTypeIds || [];
        showSkippedToast({
          action: 'assigned',
          result: {
            participants: result?.participants,
            skipped,
            requestedTypeIds: selectedScheduleTypes,
          },
          scheduleTypes: programScheduleTypes,
        });
      }
      succeeded = true;
    } catch (err) {
      message.error('Failed to assign participant');
    } finally {
      // Always reset applying state while the component is still mounted.
      // onClose() unmounts the modal (parent renders conditionally on `visible`),
      // so any setState after onClose() triggers the unmounted-component warning.
      setApplying(false);
    }
    if (succeeded) onClose();
  };

  return (
    <Modal
      width="500px"
      visible={visible}
      onCancel={onClose}
      title="Apply to which schedule types?"
      footer={[
        <Button key="cancel" onClick={onClose} disabled={applying}>
          Cancel
        </Button>,
        <Button
          key="apply"
          type="primary"
          loading={applying}
          disabled={selectedScheduleTypes.length === 0}
          onClick={handleApply}
        >
          Apply
        </Button>,
      ]}
    >
      <Checkbox.Group
        value={selectedScheduleTypes}
        onChange={(checkedValues) => setSelectedScheduleTypes(checkedValues)}
        style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
      >
        {visibleScheduleTypes.map((type) => (
          <Checkbox
            key={type.id}
            value={type.id}
            style={{
              marginLeft: 0,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <span style={{ fontSize: '14px' }}>{type.type}</span>
          </Checkbox>
        ))}
      </Checkbox.Group>
    </Modal>
  );
};

export default AddParticipantScheduleTypesModal;
